import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import type { AdminSurveyStats, AdminResultItem, ActiveFilter } from '../types/admin'
import type { Survey } from '../types/survey'
import { FilterChips } from './FilterChips'
import { QuestionChart } from './QuestionChart'
import { RespondentList } from './RespondentList'
import '../styles/analytics.css'

interface AnalyticsTabsProps {
  stats: AdminSurveyStats
  results: AdminResultItem[]
  survey: Survey | null
}

function extractQuestions(survey: Survey | null): Array<{ id: string; label: string }> {
  if (!survey?.definition) return []
  const pages = (survey.definition as any).pages
  if (!Array.isArray(pages)) return []
  const questions: Array<{ id: string; label: string }> = []
  for (const page of pages) {
    const elements = page.elements || page.questions || []
    for (const el of elements) {
      if (el.name) {
        questions.push({ id: el.name, label: el.title || el.name })
      }
    }
  }
  return questions
}

export function AnalyticsTabs({ stats, results, survey }: AnalyticsTabsProps) {
  const [activeTab, setActiveTab] = useState<'questions' | 'respondents' | null>(null)
  const [renderedTab, setRenderedTab] = useState<'questions' | 'respondents' | null>('questions')
  const [filters, setFilters] = useState<ActiveFilter[]>([])
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    if (collapseTimer.current) clearTimeout(collapseTimer.current)
    if (activeTab !== null) {
      setRenderedTab(activeTab)
    } else {
      collapseTimer.current = setTimeout(() => setRenderedTab(null), 500)
    }
    return () => { if (collapseTimer.current) clearTimeout(collapseTimer.current) }
  }, [activeTab])

  const questions = useMemo(() => extractQuestions(survey), [survey])
  const questionLabelMap = useMemo(() => new Map(questions.map((q) => [q.id, q.label])), [questions])

  const handleFilterSelect = useCallback((filter: ActiveFilter) => {
    setFilters((prev) => {
      const exists = prev.findIndex((f) => f.questionId === filter.questionId && f.value === filter.value)
      if (exists >= 0) {
        return prev.filter((_, i) => i !== exists)
      }
      return [...prev, filter]
    })
  }, [])

  const handleRemoveFilter = useCallback((index: number) => {
    setFilters((prev) => prev.filter((_, i) => i !== index))
  }, [])

  const handleClearAll = useCallback(() => setFilters([]), [])

  const textResponsesByQuestion = useMemo(() => {
    const map = new Map<string, Array<{ respondentName: string; value: string }>>()
    for (const r of results) {
      if (!r.data) continue
      for (const [qId, value] of Object.entries(r.data)) {
        if (typeof value === 'string' && value.trim()) {
          const arr = map.get(qId) || []
          arr.push({ respondentName: r.respondentName || 'Anonymous', value })
          map.set(qId, arr)
        }
      }
    }
    return map
  }, [results])

  const breakdowns = stats.questionBreakdowns || []

  return (
    <div className="analytics-tabs">
      <FilterChips filters={filters} onRemove={handleRemoveFilter} onClearAll={handleClearAll} />

      <div className="tab-toggle">
        <button
          className={`tab-button ${activeTab === 'questions' ? 'active' : ''}`}
          onClick={() => setActiveTab(activeTab === 'questions' ? null : 'questions')}
        >
          Question Analytics {activeTab === 'questions' ? '\u25B2' : '\u25BC'}
        </button>
        <button
          className={`tab-button ${activeTab === 'respondents' ? 'active' : ''}`}
          onClick={() => setActiveTab(activeTab === 'respondents' ? null : 'respondents')}
        >
          Respondents and Responses ({results.length}) {activeTab === 'respondents' ? '\u25B2' : '\u25BC'}
        </button>
      </div>

      <div className={`analytics-panel ${activeTab !== null ? 'open' : ''}`}>
        <div className="analytics-panel-inner">
          {renderedTab === 'questions' && (
            <div className="question-charts-grid">
              {breakdowns.length === 0 && (
                <p className="analytics-empty">No question data available yet.</p>
              )}
              {breakdowns.map((b) => (
                <QuestionChart
                  key={b.questionId}
                  breakdown={b}
                  questionLabel={questionLabelMap.get(b.questionId) || b.questionId}
                  textResponses={textResponsesByQuestion.get(b.questionId)}
                  onFilterSelect={handleFilterSelect}
                  activeFilters={filters}
                />
              ))}
            </div>
          )}

          {renderedTab === 'respondents' && (
            <RespondentList
              results={results}
              questions={questions}
              activeFilters={filters}
            />
          )}
        </div>
      </div>
    </div>
  )
}
